import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useArticleShares, useShareArticle, useRemoveArticleShare } from '@/queries/articles';
import XIcon from '@/assets/common/x.svg?react';
import styles from './ShareArticleModal.module.scss';

interface ShareArticleModalProps {
  articleId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ShareArticleModal({ articleId, isOpen, onClose }: ShareArticleModalProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: shares, isLoading } = useArticleShares(articleId);
  const shareArticle = useShareArticle();
  const removeShare = useRemoveArticleShare();

  const handleShare = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setError(null);
    shareArticle.mutate(
      { articleId, email: email.trim() },
      {
        onSuccess: () => {
          setEmail('');
        },
        onError: (err) => {
          setError(err.message);
        },
      }
    );
  };

  const handleRemove = (userId: string) => {
    removeShare.mutate({ articleId, userId });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={styles.modal}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className={styles.modal__header}>
              <h2 className={styles.modal__title}>Share Article</h2>
              <button onClick={onClose} className={styles.modal__close}>
                <XIcon />
              </button>
            </div>

            <form onSubmit={handleShare} className={styles.modal__form}>
              <input
                type="email"
                className={styles.modal__input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
              />
              <button
                type="submit"
                className={styles.modal__button}
                disabled={!email.trim() || shareArticle.isPending}
              >
                {shareArticle.isPending ? 'Sharing...' : 'Share'}
              </button>
            </form>

            {error && (
              <p className={styles.modal__error}>{error}</p>
            )}

            <div className={styles.modal__shares}>
              <h3 className={styles.modal__sharesTitle}>Shared with</h3>
              {isLoading ? (
                <p className={styles.modal__loading}>Loading...</p>
              ) : shares && shares.length > 0 ? (
                <ul className={styles.modal__sharesList}>
                  {shares.map((share) => (
                    <li key={share.id} className={styles.modal__shareItem}>
                      <span className={styles.modal__shareEmail}>
                        {share.shared_with_display_name || share.shared_with_email}
                      </span>
                      <button
                        onClick={() => handleRemove(share.shared_with_user_id)}
                        className={styles.modal__removeButton}
                        disabled={removeShare.isPending}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.modal__empty}>Not shared with anyone yet</p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
